import { Link } from 'react-router-dom'
import type { DisclosureAgreement } from '../../types'

export interface CrossFundDealsProps {
  disclosures: DisclosureAgreement[]
  entityIds: string[]
  /** Where "see Overlap →" goes. */
  overlapHref?: string
}

/** Identity is released only from rung 4, after a mutual NDA. */
const NAMED_RUNG = 4

export interface CrossFundCounts {
  /** Entities with at least one live disclosure outside this portfolio. */
  outwardEntityCount: number
  named: number
  anonymous: number
  betweenOwn: number
}

/**
 * Counts this portfolio's live disclosures three ways. The lead sentence
 * counts ENTITIES, not agreements — several disclosures can belong to one
 * entity. Exported so the numbers can be tested without a DOM.
 */
export function countCrossFundDeals(
  disclosures: DisclosureAgreement[],
  entityIds: string[],
): CrossFundCounts {
  const own = new Set(entityIds)
  const betweenOwn = disclosures.filter((d) => own.has(d.org_a_id) && own.has(d.org_b_id))
  const betweenOwnIds = new Set(betweenOwn.map((d) => d.agreement_id))
  const outward = disclosures.filter((d) => !betweenOwnIds.has(d.agreement_id))

  const ownSideOf = (d: DisclosureAgreement): string | undefined => {
    if (d.my_org_id && own.has(d.my_org_id)) return d.my_org_id
    if (own.has(d.org_a_id)) return d.org_a_id
    if (own.has(d.org_b_id)) return d.org_b_id
    return undefined
  }

  return {
    outwardEntityCount: new Set(outward.map(ownSideOf).filter((id): id is string => !!id)).size,
    named: outward.filter((d) => d.current_level >= NAMED_RUNG).length,
    anonymous: outward.filter((d) => d.current_level < NAMED_RUNG).length,
    betweenOwn: betweenOwn.length,
  }
}

function Row({ label, value, title }: { label: string; value: number; title?: string }) {
  return (
    <div className="flex items-center justify-between gap-3 border-t border-iflow-900/5 py-2 first:border-t-0">
      <span className="text-[12.5px] text-gray-500" title={title}>{label}</span>
      <span className="text-[12.5px] font-semibold text-iflow-900">{value}</span>
    </div>
  )
}

/**
 * Figure S7's "🤝 Cross-fund deals" card. This level watches and never
 * advances a ladder, so the card carries no action beyond a cross-link.
 * Identity only appears from rung 4 after a mutual NDA; below that the card
 * counts the counterparty and never names it.
 */
export default function CrossFundDeals({
  disclosures,
  entityIds,
  overlapHref = '/agg?view=overlap',
}: CrossFundDealsProps) {
  const counts = countCrossFundDeals(disclosures, entityIds)

  return (
    <div data-part="CrossFundDeals" className="light-card p-4">
      <h3 className="text-[15px] font-bold text-iflow-900">🤝 Cross-fund deals</h3>

      <p className="mt-1 text-[13px] leading-relaxed text-gray-700">
        <b className="text-iflow-900">
          {counts.outwardEntityCount} of your {counts.outwardEntityCount === 1 ? 'entities has' : 'entities have'}
        </b>{' '}
        live disclosures with counterparties outside this portfolio.
      </p>

      <div className="mt-2">
        <Row label="Named (rung ≥ 4)" value={counts.named} />
        <Row label="Anonymous counterparties" value={counts.anonymous} />
        <Row
          label="Between two of your own entities"
          value={counts.betweenOwn}
          title="The inside-portfolio case is a duplicated-effort question, not a cross-fund one"
        />
      </div>

      <div className="mt-2 text-right">
        <Link to={overlapHref} className="text-[12px] font-semibold text-iflow-700 hover:text-iflow-800 hover:underline">
          see Overlap →
        </Link>
      </div>
    </div>
  )
}
